// Вкладка "Нагрев (длительно допустимый ток)"

import { useCallback } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { InputPanel } from '../input/InputPanel';
import { NumberInput, SelectField } from '../input';
import { Button, ExportButton } from '../buttons';
import { ResultPanel, StatusIndicator, SectionGrid } from '../results';
import { 
  STANDARD_SECTIONS, 
  MATERIAL_LABELS, 
  INSTALLATION_METHOD_LABELS,
  INSULATION_TYPE_LABELS,
} from '../../data/constants';
import { 
  calculateHeating, 
  selectSectionByHeating,
  calculateCorrectionFactor,
} from '../../utils/heating';
import { Flame, RotateCcw, Wrench } from 'lucide-react';
import type { Material, InstallationMethod, InsulationType } from '../../types';

// Опции для селектов
const materialOptions = Object.entries(MATERIAL_LABELS).map(([value, label]) => ({
  value: value as Material,
  label
}));

const installationMethodOptions = Object.entries(INSTALLATION_METHOD_LABELS).map(([value, label]) => ({
  value: value as InstallationMethod,
  label
}));

const insulationTypeOptions = Object.entries(INSULATION_TYPE_LABELS).map(([value, label]) => ({
  value: value as InsulationType,
  label
}));

// Опции для теплового сопротивления грунта
const thermalResistanceOptions = [
  { value: '50', label: '50 К·см/Вт (влажный)' },
  { value: '80', label: '80 К·см/Вт (сырой)' },
  { value: '120', label: '120 К·см/Вт (нормальный)' },
  { value: '160', label: '160 К·см/Вт (сухой)' },
  { value: '200', label: '200 К·см/Вт (очень сухой)' },
];

export function HeatingTab() {
  const {
    input,
    heatingResult,
    setInput,
    setHeatingResult,
    reset,
  } = useCalculator();

  // Обработчик расчёта
  const handleCalculate = useCallback(() => {
    const result = calculateHeating(input);
    setHeatingResult(result);
  }, [input, setHeatingResult]);

  // Обработчик подбора сечения
  const handleSelectSection = useCallback(() => {
    const thermalResistance = input.thermalResistance ?? 120;
    
    // Рассчитываем поправочный коэффициент
    const correctionFactor = calculateCorrectionFactor(
      input.insulationType,
      input.ambientTemp,
      input.cablesCount,
      input.installationMethod,
      thermalResistance
    );

    // Подбираем сечение
    const recommendedSection = selectSectionByHeating(
      input.current,
      input.material,
      input.insulationType,
      input.installationMethod,
      correctionFactor
    );
    
    if (recommendedSection !== null) {
      setInput({ crossSection: recommendedSection });
      // Автоматически пересчитываем с новым сечением
      const newInput = { ...input, crossSection: recommendedSection };
      const result = calculateHeating(newInput);
      setHeatingResult(result);
    }
  }, [input, setInput, setHeatingResult]);

  // Обработчик сброса
  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  // Обработчик изменения сечения
  const handleSectionChange = useCallback((section: number) => {
    setInput({ crossSection: section });
  }, [setInput]);

  // Проверка, нужно ли показывать поле теплового сопротивления
  const showThermalResistance = input.installationMethod === 'ground';

  return (
    <div className="heating-tab">
      <div className="heating-tab__input">
        {/* Все параметры в одной панели */}
        <InputPanel title="Параметры расчёта">
          <div className="input-grid">
            <NumberInput
              label="Ток"
              value={input.current}
              onChange={(value) => setInput({ current: value })}
              unit="А"
              min={0}
              step={0.1}
              required
              tooltip="Расчётный ток нагрузки"
            />
            <SelectField
              label="Материал"
              value={input.material}
              onChange={(value) => setInput({ material: value as Material })}
              options={materialOptions}
            />
            <SelectField
              label="Прокладка"
              value={input.installationMethod}
              onChange={(value) => setInput({ installationMethod: value as InstallationMethod })}
              options={installationMethodOptions}
            />
            <SelectField
              label="Изоляция"
              value={input.insulationType}
              onChange={(value) => setInput({ insulationType: value as InsulationType })}
              options={insulationTypeOptions}
            />
            <NumberInput
              label="T среды"
              value={input.ambientTemp}
              onChange={(value) => setInput({ ambientTemp: value })}
              unit="°C"
              min={-20}
              max={70}
              step={1}
              tooltip="Температура окружающей среды"
            />
            <NumberInput
              label="Кабелей"
              value={input.cablesCount}
              onChange={(value) => setInput({ cablesCount: Math.max(1, Math.min(10, value)) })}
              unit="шт"
              min={1}
              max={10}
              step={1}
              tooltip="Количество кабелей рядом"
            />
            {showThermalResistance && (
              <SelectField
                label="R грунта"
                value={String(input.thermalResistance ?? 120)}
                onChange={(value) => setInput({ thermalResistance: Number(value) })}
                options={thermalResistanceOptions}
              />
            )}
          </div>
        </InputPanel>

        {/* Выбор сечения */}
        <InputPanel title="Сечение жилы">
          <SectionGrid
            sections={STANDARD_SECTIONS}
            selectedSection={input.crossSection}
            onSectionChange={handleSectionChange}
          />
        </InputPanel>

        {/* Кнопки управления */}
        <div className="heating-tab__actions">
          <Button
            variant="primary"
            size="sm"
            icon={<Flame size={16} />}
            onClick={handleCalculate}
            disabled={input.current <= 0}
          >
            Расчёт
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Wrench size={16} />}
            onClick={handleSelectSection}
            disabled={input.current <= 0}
          >
            Подобрать
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<RotateCcw size={16} />}
            onClick={handleReset}
          >
            Сброс
          </Button>
          <ExportButton
            mode="heating"
            input={input}
            heatingResult={heatingResult}
          />
        </div>
      </div>

      {/* Панель результатов */}
      <div className="heating-tab__results">
        {heatingResult ? (
          <ResultPanel
            title="Результаты проверки по нагреву"
            status={
              <StatusIndicator status={heatingResult.isWithinNorm ? 'success' : 'danger'}>
                {heatingResult.isWithinNorm ? 'Проходит по нагреву' : 'Превышение допустимого тока'}
              </StatusIndicator>
            }
            rows={[
              { 
                label: 'Расчётный ток', 
                value: heatingResult.calculatedCurrent.toFixed(1), 
                unit: 'А' 
              },
              { 
                label: 'Допустимый ток (табл.)', 
                value: heatingResult.allowedCurrent.toFixed(0), 
                unit: 'А' 
              },
              { 
                label: 'Поправочный коэффициент', 
                value: heatingResult.correctionFactor.toFixed(3), 
                unit: '' 
              },
              { 
                label: 'Скорректированный Iдоп', 
                value: heatingResult.correctedAllowedCurrent.toFixed(1), 
                unit: 'А',
                highlight: heatingResult.isWithinNorm ? 'success' : 'danger'
              },
              { 
                label: 'Запас по току', 
                value: heatingResult.currentMargin >= 0 
                  ? `+${heatingResult.currentMargin.toFixed(1)}` 
                  : heatingResult.currentMargin.toFixed(1), 
                unit: '%',
                highlight: heatingResult.currentMargin >= 0 ? 'success' : 'danger'
              },
              {
                label: 'Рекомендуемое сечение',
                value: heatingResult.recommendedSection.toFixed(1),
                unit: 'мм²'
              },
            ]}
          />
        ) : (
          <div className="empty-state">
            <Flame className="empty-state__icon" />
            <p className="empty-state__text">
              Введите параметры и нажмите "Рассчитать" для проверки по нагреву
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default HeatingTab;
